import { NgTemplateOutlet } from '@angular/common';
import { Component, computed, input, output, ChangeDetectionStrategy } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';

import { InfoboxComponent } from '@core/components-infobox-legacy';
import { injectTranslate, TranslatePipe } from '@core/i18n-legacy';
import { InviteExistingMemberForm, InviteMethod, TellentMember } from '@core/tellent-types';
import { InviteMethodSelectComponent } from '@core/tellent-ui';
import { resolveMemberFullName } from '@core/tellent-util';
import {
  AvatarComponent,
  FormErrorDirective,
  FormFieldComponent,
  IconComponent,
  InputComponent,
  LabelComponent,
  ProtectedControlDirective,
  SpinnerComponent,
} from '@core/user-interface-legacy';

/*
  basic - member has work email, display only card with details
  advanced - member has no work email, display advanced member details form (invite via email/generate username and password)
 */
type FormVariant = 'basic' | 'advanced';

@Component({
  selector: 'tlc-invite-existing-member-details-form',
  templateUrl: './invite-existing-member-details-form.component.html',
  styleUrls: ['./invite-existing-member-details-form.component.less'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    AvatarComponent,
    NgTemplateOutlet,
    ReactiveFormsModule,
    InfoboxComponent,
    TranslatePipe,
    FormFieldComponent,
    InputComponent,
    LabelComponent,
    ProtectedControlDirective,
    FormErrorDirective,
    SpinnerComponent,
    InviteMethodSelectComponent,
    IconComponent,
  ],
})
export class InviteExistingMemberDetailsFormComponent {
  private readonly t = injectTranslate();

  public memberData = input.required<TellentMember>();
  public parentForm = input.required<FormGroup<InviteExistingMemberForm>>();
  public displayAdvancedForm = input<boolean>(false);

  public importCsvClick = output<void>();

  protected InviteMethod = InviteMethod;

  protected formVariant = computed<FormVariant>(() => {
    const member = this.memberData();
    return member && member.hasWorkEmail && member.email ? 'basic' : 'advanced';
  });

  protected fullName = computed<string>(() => {
    const member = this.memberData();
    return member ? resolveMemberFullName(member) : '';
  });

  protected emailFieldErrors = {
    required: this.t('tlc.common.errors.required'),
    email: this.t('tlc.common.errors.email.format'),
    duplicated: this.t('tlc.common.errors.duplicated_email'),
    existingMember: this.t('tlc.common.errors.existing_member'),
  };

  public get inviteMethodControl(): FormControl<InviteMethod | null> {
    return this.parentForm().controls.inviteMethod;
  }

  public navigateToCsvImport(): void {
    this.importCsvClick.emit();
  }
}
